import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * `ComponentPropsWithRef` — `ref` ham qabul qilinadi.
 *
 * React 19 da `ref` oddiy propga aylandi, ya'ni `forwardRef` shart emas.
 * Kerak bo'lgan joyi: qidiruv maydoni («/» tugmasi fokusni shu yerga
 * qaytaradi, GAP_ANALYSIS §C3) va forma xatosida birinchi buzuq maydonga
 * o'tish — ikkalasi ham DOM elementiga to'g'ridan-to'g'ri murojaatni talab qiladi.
 */
export interface InputProps extends ComponentPropsWithRef<'input'> {
  readonly invalid?: boolean;
  /**
   * Raqamli maydon — o'ng tomonga tekislanadi va monospace bo'ladi.
   * Miqdor/summa ustunlari ustma-ust tushishi kerak (GAP_ANALYSIS §C3).
   */
  readonly numeric?: boolean;
  /**
   * Maydon ostidagi tushuntirish (masalan «bo'sh qoldirilsa avtomat raqam»).
   *
   * Nima uchun komponent ichida, ekranda qo'lda emas: tushuntirish
   * `aria-describedby` orqali maydonga BOG'LANISHI kerak, aks holda
   * skrinrider foydalanuvchi uni umuman eshitmaydi. Qo'lda yozilganda esa
   * `id` deyarli har doim unutilardi.
   */
  readonly description?: ReactNode;
  /**
   * Xato matni. Berilishi maydonni avtomat `invalid` qiladi — ikkita
   * propni qo'lda muvofiqlashtirib yurish shart emas va «qizil ramka bor,
   * sababi yo'q» holati yuzaga kelmaydi.
   */
  readonly error?: ReactNode;
  /** `description`/`error` bo'lganda paydo bo'ladigan o'ram uchun sinf. */
  readonly containerClassName?: string;
}

/**
 * Maydon (input/select) uchun umumiy ko'rinish.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ MAYDON TUGMA BILAN BIR BALANDLIKDA                                      ║
 * ║                                                                          ║
 * ║ Filtr qatorida `Input`, `Select` va `Button` yonma-yon turadi. Ular      ║
 * ║ 40px da qotirilgan (`h-10` = `Button` ning `md` o'lchami) — bittasi      ║
 * ║ 38px bo'lsa ham qator «tishlagan» ko'rinadi va ekran shoshib yig'ilgan   ║
 * ║ taassurot beradi.                                                        ║
 * ║                                                                          ║
 * ║ Fokus: `styles.css` dagi global halqa 1px offset bilan chiziladi va u    ║
 * ║ maydon ramkasiga tegib turardi — qaysi maydon faol ekani zo'rg'a         ║
 * ║ bilinardi. Bu yerda 2px offset + ramka rangining o'zgarishi: klaviatura  ║
 * ║ bilan formani to'ldirayotgan odam qayerda turganini bir qarashda ko'radi.║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function fieldClasses(invalid: boolean | undefined, className?: string): string {
  return cn(
    'h-10 w-full rounded-md bg-surface px-3 text-sm text-ink',
    'placeholder:text-ink-muted transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    /*
     * O'CHIRILGAN maydon — «o'qish uchun» ko'rinadi, yo'qolmaydi.
     * `opacity-60` matnni ham xiralashtirardi; endi fon boshqacha
     * (`surface-2`) va matn to'liq o'qiladi: qiymat ko'rinishi kerak,
     * faqat tahrirlab bo'lmaydi.
     */
    'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-muted',
    /*
     * Ramka kengligi HAR IKKI holatda ham aniq yoziladi (`border-2` /
     * `border`). Umumiy qatorda `border` qoldirib, faqat xatoda `border-2`
     * qo'shish rad etildi: `cn()` — oddiy birlashtirish, Tailwind sinflarini
     * BIRLASHTIRMAYDI, ya'ni ikkala kenglik ham CSS ga tushib, qaysi biri
     * ustun kelishini faylning tartibi hal qilardi.
     */
    invalid
      ? // Xato ramkasi QALIN: nozik qizil chiziq to'ldirilgan formada ko'zga tashlanmasdi.
        'border-2 border-danger focus-visible:outline-danger'
      : 'border border-line focus-visible:border-brand focus-visible:outline-brand',
    className,
  );
}

export function Input({
  className,
  containerClassName,
  invalid,
  numeric,
  description,
  error,
  id,
  ...props
}: InputProps): ReactNode {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hasError = error !== undefined && error !== null && error !== false && error !== '';
  const hasDescription = description !== undefined && description !== null && description !== false;
  const isInvalid = invalid === true || hasError;

  const field = (
    <input
      id={id ?? (hasError || hasDescription ? fieldId : undefined)}
      aria-invalid={isInvalid || undefined}
      /*
       * Xato bo'lsa faqat XATO o'qiladi, tushuntirish emas: skrinrider
       * ikkalasini ketma-ket aytsa, eng muhim jumla oxirida qolib ketardi.
       */
      aria-describedby={hasError ? `${fieldId}-error` : hasDescription ? `${fieldId}-description` : undefined}
      className={fieldClasses(isInvalid, cn(numeric && 'evo-num', className))}
      {...props}
    />
  );

  // Qo'shimcha matn yo'q bo'lsa — o'ram ham yo'q. Eski chaqiruv joylarining
  // maketi (grid, flex, `col-span`) bitta ortiqcha `div` dan buzilmaydi.
  if (!hasError && !hasDescription) return field;

  return (
    <div className={cn('flex flex-col gap-1', containerClassName)}>
      {field}
      {hasError ? (
        <p id={`${fieldId}-error`} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : (
        <p id={`${fieldId}-description`} className="text-xs text-ink-muted">
          {description}
        </p>
      )}
    </div>
  );
}
